import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building, Users, Award, Target, ArrowRight, Heart, Eye, Lightbulb, Shield, Globe, MapPin } from 'lucide-react';

// Define reusable animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
    }
  }
};

const About = () => {
  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-teal-50/30">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              About Mediquip & Leadership
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Learn about our comprehensive medical services company, our vision and mission, core values, 
              and the experienced leadership that forms the strategic foundation of our operations across 
              Anglophone and Francophone countries.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Content */}
            <motion.div className="space-y-8" variants={containerVariants}>
              <div className="space-y-6">
                <motion.h2 className="text-3xl font-bold text-slate-900" variants={itemVariants}>
                  About Mediquip
                </motion.h2>
                
                <motion.p className="text-lg text-slate-700 leading-relaxed" variants={itemVariants}>
                  Mediquip Nigeria Limited is a comprehensive medical services company that operates across 
                  Anglophone and Francophone countries, delivering three core services: medical consumables 
                  distribution, healthcare consulting, and strategic business development. Our company was 
                  established to build upon the extensive expertise and proven track record of our directors, 
                  who bring distinguished experience in the medical field.
                </motion.p>
                
                <motion.p className="text-lg text-slate-700 leading-relaxed" variants={itemVariants}>
                  Our directors' remarkable careers span clinical excellence and corporate healthcare 
                  leadership, and Mediquip operates as an independent entity that strategically utilizes their expertise 
                  to serve healthcare practitioners and organizations. We bridge the gap between healthcare 
                  practitioners and premium-quality medical consumables while providing expert consulting services 
                  to optimize operations and strategic business development solutions to drive organizational growth.
                </motion.p>

                <motion.p className="text-lg text-slate-700 leading-relaxed" variants={itemVariants}>
                  Our unique positioning allows us to serve diverse markets across both English-speaking and 
                  French-speaking territories, understanding the distinct regulatory environments, cultural nuances, 
                  and healthcare challenges faced by medical professionals in these regions. Mediquip combines 
                  proven industry expertise with comprehensive service offerings tailored to meet the evolving 
                  needs of healthcare practitioners and institutions across our target markets.
                </motion.p>
              </div>

              <motion.div className="flex flex-col sm:flex-row gap-4" variants={itemVariants}>
                <Link
                  to="/leadership"
                  className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200"
                >
                  Meet Our Leadership
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <Link
                  to="/vision-mission"
                  className="inline-flex items-center px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:border-teal-600 hover:text-teal-600 transition-all duration-200"
                >
                  Our Vision & Mission
                </Link>
              </motion.div>
            </motion.div>

            {/* Images Grid */}
            <motion.div
              className="relative"
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ amount: 0.5 }}
              transition={{ duration: 0.8 }}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src="https://images.unsplash.com/photo-1561328165-f0b762a9508e"
                    alt="Medical supplies organization"
                    className="w-full h-48 object-cover rounded-xl shadow-lg"
                    loading='lazy'
                  />
                  <img
                    src="https://images.unsplash.com/photo-1576091160550-2173dba999ef"
                    alt="Healthcare technology"
                    className="w-full h-32 object-cover rounded-xl shadow-lg"
                    loading='lazy'
                  />
                </div>
                
                <div className="space-y-4 mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1581056771085-3ce30d907416"
                    alt="Medical storage facility"
                    className="w-full h-32 object-cover rounded-xl shadow-lg"
                    loading='lazy'
                  />
                  <img
                    src="https://images.unsplash.com/photo-1605176173609-a0067079b419"
                    alt="Medical consumables"
                    className="w-full h-48 object-cover rounded-xl shadow-lg"
                    loading='lazy'
                  />
                </div>
              </div>
              
              {/* Background decoration */}
              <div className="absolute -inset-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl -z-10 opacity-50"></div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      ---

      {/* Vision & Mission Section */}
      <section id="vision-mission" className="py-20 bg-white">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="text-center mb-16">
            <motion.h2 className="text-4xl font-bold text-slate-900 mb-4" variants={itemVariants}>
              Our Vision & Mission
            </motion.h2>
            <motion.p className="text-xl text-slate-600 max-w-3xl mx-auto" variants={itemVariants}>
              Our vision and mission guide Mediquip's operations across Anglophone and Francophone countries, 
              supported by core values and proven medical expertise.
            </motion.p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-20">
            {/* Vision */}
            <motion.div
              className="bg-slate-50 rounded-2xl p-8 lg:p-12 shadow-lg border border-slate-100 relative overflow-hidden"
              variants={itemVariants}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100 to-teal-50 rounded-bl-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-teal-100 rounded-xl flex items-center justify-center mb-8">
                  <Eye className="w-10 h-10 text-teal-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-6">Our Vision</h3>
                <p className="text-xl text-slate-700 leading-relaxed mb-8">
                  To be the leading comprehensive medical services company across Anglophone and Francophone 
                  countries, recognized for excellence in medical consumables distribution, healthcare consulting, 
                  and strategic business development solutions.
                </p>
                <Link
                  to="/services"
                  className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold transition-colors"
                >
                  Learn About Our Services <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              className="bg-slate-50 rounded-2xl p-8 lg:p-12 shadow-lg border border-slate-100 relative overflow-hidden"
              variants={itemVariants}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-blue-50 rounded-bl-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-blue-100 rounded-xl flex items-center justify-center mb-8">
                  <Target className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-6">Our Mission</h3>
                <div className="space-y-4 text-slate-700 mb-8">
                  <p className="flex items-start text-lg">
                    <span className="w-3 h-3 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></span>
                    Delivering premium medical consumables directly to healthcare practitioners across diverse markets
                  </p>
                  <p className="flex items-start text-lg">
                    <span className="w-3 h-3 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></span>
                    Providing expert healthcare consulting to optimize operations and improve patient care delivery
                  </p>
                  <p className="flex items-start text-lg">
                    <span className="w-3 h-3 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></span>
                    Offering strategic business development solutions to help healthcare organizations grow and expand
                  </p>
                  <p className="flex items-start text-lg">
                    <span className="w-3 h-3 bg-blue-600 rounded-full mt-2 mr-4 flex-shrink-0"></span>
                    Building on proven medical expertise to serve both Anglophone and Francophone countries
                  </p>
                </div>
                <Link
                  to="/services"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                >
                  Explore Our Solutions <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Core Values */}
          <div className="text-center mb-16">
            <motion.h3 className="text-3xl font-bold text-slate-900 mb-6" variants={itemVariants}>Our Core Values</motion.h3>
            <motion.p className="text-xl text-slate-600 max-w-3xl mx-auto" variants={itemVariants}>
              These values guide Mediquip's operations and decision-making as we deliver comprehensive 
              healthcare solutions across Anglophone and Francophone markets.
            </motion.p>
          </div>

          <motion.div className="grid md:grid-cols-3 gap-8" variants={containerVariants}>
            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              variants={itemVariants}
            >
              <div className="w-20 h-20 bg-teal-100 text-teal-600 border-teal-200 rounded-xl flex items-center justify-center mb-6 border-2">
                <Shield className="w-10 h-10" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Integrity</h4>
              <p className="text-slate-600 leading-relaxed text-lg">Upholding the highest ethical standards in every aspect of our business.</p>
            </motion.div>

            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              variants={itemVariants}
            >
              <div className="w-20 h-20 bg-blue-100 text-blue-600 border-blue-200 rounded-xl flex items-center justify-center mb-6 border-2">
                <Lightbulb className="w-10 h-10" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Innovation</h4>
              <p className="text-slate-600 leading-relaxed text-lg">Bringing cutting-edge solutions to healthcare providers.</p>
            </motion.div>

            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              variants={itemVariants}
            >
              <div className="w-20 h-20 bg-green-100 text-green-600 border-green-200 rounded-xl flex items-center justify-center mb-6 border-2">
                <Award className="w-10 h-10" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Excellence</h4>
              <p className="text-slate-600 leading-relaxed text-lg">Exceeding expectations in service delivery and product quality.</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      ---

      {/* Leadership Section */}
      <section id="leadership" className="py-20 bg-slate-50">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="text-center mb-16">
            <motion.h2 className="text-4xl font-bold text-slate-900 mb-4" variants={itemVariants}>
              Leadership Team
            </motion.h2>
            <motion.p className="text-xl text-slate-600 max-w-3xl mx-auto" variants={itemVariants}>
              Meet our dedicated directors who bring extensive healthcare expertise and strategic vision 
              to drive Mediquip's mission across Anglophone and Francophone countries.
            </motion.p>
          </div>

          <motion.div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto" variants={containerVariants}>
            {/* Director 1 - Fola Akinrinola */}
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 text-center"
              variants={itemVariants}
            >
              <div className="w-40 h-40 rounded-full overflow-hidden mx-auto mb-6 shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_healthcare-dist/artifacts/8e55dqsq_Fola%20Akinrinlola.jpg"
                  alt="Fola Akinrinlola"
                  className="w-full h-full object-cover object-top"
                  loading='lazy'
                />
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Fola Akinrinlola
              </h3>
              <p className="text-teal-600 font-semibold mb-4 text-lg">
                Director
              </p>

              <div className="flex items-center justify-center space-x-2 text-slate-600 text-sm">
                <MapPin className="w-4 h-4" />
                <span>Lagos, Nigeria</span>
              </div>
            </motion.div>

            {/* Director 2 - Feyisola Akinrinola */}
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 text-center"
              variants={itemVariants}
            >
              <div className="w-40 h-40 rounded-full overflow-hidden mx-auto mb-6 shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_healthcare-dist/artifacts/xcd6l949_Feyisola%20Akinrinlola.jpg"
                  alt="Feyisola Akinrinlola"
                  className="w-full h-full object-cover object-top scale-150"
                  loading='lazy'
                />
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Feyisola Akinrinlola
              </h3>
              <p className="text-blue-600 font-semibold mb-4 text-lg">
                Director
              </p>

              <div className="flex items-center justify-center space-x-2 text-slate-600 text-sm">
                <MapPin className="w-4 h-4" />
                <span>Lagos, Nigeria</span>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      ---

      {/* Strategic Partnerships */}
      <section className="py-16 bg-white">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="text-center mb-12">
            <motion.h2 className="text-3xl font-bold text-slate-900 mb-4" variants={itemVariants}>Our Strategic Partnerships</motion.h2>
            <motion.p className="text-lg text-slate-600 max-w-3xl mx-auto" variants={itemVariants}>
              Mediquip has established key partnerships with leading global healthcare companies to bring 
              world-class medical consumables and technologies to West African healthcare providers.
            </motion.p>
          </div>

          <motion.div className="grid md:grid-cols-3 gap-8" variants={containerVariants}>
            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 text-center"
              variants={itemVariants}
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Lapu Medical Technology</h3>
              <p className="text-teal-600 font-semibold mb-3">Official Partner</p>
              <p className="text-slate-600">
                Cardiovascular Medical Device Innovation
              </p>
            </motion.div>

            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 text-center"
              variants={itemVariants}
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Wizbiotech</h3>
              <p className="text-blue-600 font-semibold mb-3">Official Partner</p>
              <p className="text-slate-600">
                Manufacturers of Diabench 5-in-1 Rapid Blood Type and Infectious combo kit
              </p>
            </motion.div>

             <motion.div
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 text-center"
              variants={itemVariants}
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Zutron Pharmaceuticals</h3>
              <p className="text-blue-600 font-semibold mb-3">Official Partner</p>
              <p className="text-slate-600">
                Distribution of HemoType Sc
              </p>
            </motion.div>

            <motion.div
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 text-center"
              variants={itemVariants}
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Terumo Blood and Cell Technologies</h3>
              <p className="text-green-600 font-semibold mb-3">Sub Dealer</p>
              <p className="text-slate-600">
                Authorized sub dealer for blood and cell technology solutions in West Africa
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      ---

      {/* Key Features */}
      <section className="py-16 bg-slate-50">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <motion.div className="text-center space-y-4" variants={itemVariants}>
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mx-auto">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Leadership Expertise</h3>
              <p className="text-slate-600">
                Built on our directors' combined decades of medical field experience and proven track record
              </p>
            </motion.div>
            
            <motion.div className="text-center space-y-4" variants={itemVariants}>
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto">
                <Award className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Comprehensive Services</h3>
              <p className="text-slate-600">
                Medical consumables distribution, healthcare consulting, and business development
              </p>
            </motion.div>
            
            <motion.div className="text-center space-y-4" variants={itemVariants}>
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto">
                <Target className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Multilingual Markets</h3>
              <p className="text-slate-600">
                Serving both Anglophone and Francophone countries with cultural understanding
              </p>
            </motion.div>
            
            <motion.div className="text-center space-y-4" variants={itemVariants}>
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mx-auto">
                <Building className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Strategic Partnerships</h3>
              <p className="text-slate-600">
                Global partnerships with Lapu Medical, Wizbiotech, Zutron Pharmaceuticals and Terumo</p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      ---

      {/* Future Outlook */}
      <section className="py-20 bg-slate-50">
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-slate-100">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Our Vision for Growth
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              Mediquip is committed to expanding our comprehensive service offerings across Anglophone and 
              Francophone countries. We aim to strengthen our distribution network, enhance our consulting 
              capabilities, and establish strategic partnerships with leading global healthcare manufacturers 
              to bring cutting-edge medical consumables and innovative business solutions to healthcare 
              practitioners and organizations throughout our target markets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200"
              >
                Explore Our Services
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:border-teal-600 hover:text-teal-600 transition-all duration-200"
              >
                Partner With Us
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;